import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { DeleteOutline, InfoOutlined } from "@mui/icons-material";
import { useState } from "react";

import UserAvatar from "components/common/UserAvatar";
import type { SecurePerson } from "features/secureUploads/secureUploads";

import { memberPickerEmptyText } from "./projectModel";

interface ProjectAccessEditorProps {
  candidates: SecurePerson[];
  selected: number[];
  onChange: (ids: number[]) => void;
  /** The Confidential Access tag is for staff only. */
  showConfidential: boolean;
  disabled?: boolean;
  loading?: boolean;
}

export function ConfidentialTag() {
  return (
    <Chip
      label="Confidential Access"
      size="small"
      sx={{
        height: 18,
        fontSize: 10,
        fontWeight: 600,
        bgcolor: "#FDECEA",
        color: "#C62828",
      }}
    />
  );
}

/** The client users on a project: a picker, a reminder of who is listed, and the
 * people chosen so far. */
export default function ProjectAccessEditor({
  candidates,
  selected,
  onChange,
  showConfidential,
  disabled = false,
  loading = false,
}: ProjectAccessEditorProps) {
  const chosen = new Set(selected);
  const people = selected
    .map((id) => candidates.find((person) => person.id === id))
    .filter((person): person is SecurePerson => Boolean(person));
  const options = candidates.filter((person) => !chosen.has(person.id));
  // A pick empties the field: the name left in it would filter the next pick.
  const [query, setQuery] = useState("");

  return (
    <Box>
      <Autocomplete<SecurePerson>
        options={options}
        value={null}
        inputValue={query}
        onInputChange={(_, next, reason) =>
          setQuery(reason === "reset" ? "" : next)
        }
        loading={loading}
        disabled={disabled}
        onChange={(_, person) => {
          if (person) onChange([...selected, person.id]);
        }}
        getOptionLabel={(person) => person.name}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderOption={(props, person) => (
          <Box component="li" {...props} key={person.id} sx={{ gap: 1 }}>
            <UserAvatar person={person} size={24} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2">{person.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {person.email}
              </Typography>
            </Box>
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Select client users"
          />
        )}
        noOptionsText={memberPickerEmptyText(candidates.length, options.length)}
      />
      <Box
        sx={{
          mt: 1.5,
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          bgcolor: "#E8F4FA",
          borderRadius: 1,
        }}
      >
        <InfoOutlined sx={{ fontSize: 16, color: "#185059" }} />
        <Typography variant="caption" sx={{ color: "#185059" }}>
          Only client users with Secure Exchange access are listed. If someone
          is missing, enable their access in Client & User Setup.
        </Typography>
      </Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
        Assigned Users
      </Typography>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{
          display: "block",
          pb: 0.75,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        User Names
      </Typography>
      {people.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
          No client users on this project yet.
        </Typography>
      ) : (
        people.map((person) => (
          <Box
            key={person.id}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              py: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            <UserAvatar person={person} size={28} />
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap>
                {person.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {person.email}
              </Typography>
            </Box>
            {showConfidential && person.confidential ? (
              <ConfidentialTag />
            ) : null}
            <IconButton
              size="small"
              aria-label={`Remove ${person.name}`}
              disabled={disabled}
              onClick={() =>
                onChange(selected.filter((id) => id !== person.id))
              }
            >
              <DeleteOutline fontSize="small" />
            </IconButton>
          </Box>
        ))
      )}
    </Box>
  );
}
